import * as React from "react";
import { Button } from "../../Button";
import { z } from "zod";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import _ from "lodash";
import { Autocomplete, Option, Dialog, DialogActions, DialogContent, DialogTitleWithCloseButton, ConfirmationDialog } from "../../../../shared/components";

interface RemoveDialogProps {
    entityName: string;
    allEntities: Option[];
    availableEntities: Option[];
    selectedValue?: Option;
    acceptCallback: (args: Object) => void;
    closeCallback: () => void;
}

function RemoveDialog(props: RemoveDialogProps) {
    const { entityName, allEntities, availableEntities, selectedValue: _selectedValue, acceptCallback, closeCallback } = props;

    const schema = z.object({
        entity: z.object({
            value: z.string(),
            label: z.string()
        }, {
            error: `Please select a ${entityName} to remove.`
        })
    });
    type FormValues = z.infer<typeof schema>;

    const selectedValue = availableEntities.find(e => _.isEqual(e?.value, _selectedValue?.value));
    const { handleSubmit, control } = useForm({ resolver: zodResolver(schema), defaultValues: { entity: selectedValue } });

    const [onRemoveCallback, setOnRemoveCallback] = React.useState(null);
    const [confirmationLabel, setConfirmationLabel] = React.useState("");
    const [showConfirmation, setShowConfirmation] = React.useState(false);

    const onSubmit: SubmitHandler<FormValues> = formModel => {
        const onRemoveCallback = () => acceptCallback({
            entity_id: formModel.entity.value,
            entity_label: formModel.entity.label
        });
        setOnRemoveCallback(() => onRemoveCallback);
        setConfirmationLabel(formModel.entity.label);
        setShowConfirmation(true);
    };

    return (
        <>
            <Dialog
                open={true}
                onClose={(_event, reason) => {
                    if (reason !== 'backdropClick') {
                        closeCallback();
                    }
                }}
                disablePortal={true}
                aria-labelledby="form-dialog-title">
                <DialogTitleWithCloseButton onClose={closeCallback} id="form-dialog-title">
                    Remove {entityName}
                </DialogTitleWithCloseButton>
                <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                    <DialogContent>
                        <div className="vertical-form-field">
                            Select {entityName} to remove:
                        </div>
                        <Controller
                            control={control}
                            name="entity"
                            render={({
                                field: { value, onChange },
                                fieldState: { error }
                            }) => (
                                <Autocomplete
                                    value={value}
                                    onChange={(_event, option, _reason) => onChange((option as Option))}
                                    error={!!error}
                                    helperText={error?.message}
                                    isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                                    getOptionLabel={(option: any) => option?.label || ""}
                                    size="small"
                                    fullWidth
                                    options={availableEntities}
                                    disableClearable
                                />
                            )}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" variant="text" color="warning">
                            Remove
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {
                showConfirmation &&
                <ConfirmationDialog
                    title={`Remove ${entityName}?`}
                    open={showConfirmation}
                    setOpen={setShowConfirmation}
                    onConfirm={onRemoveCallback}
                >
                    <h3>Warning:</h3>
                    <br />
                    This operation will permanently remove the {entityName} named '{confirmationLabel}'. This cannot be reversed!
                    <br />
                    <br />
                </ConfirmationDialog>
            }
        </>
    );
}

export { RemoveDialog };